import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const CategoryCarousel = ({ categories }) => {
  const [itemsPerView, setItemsPerView] = useState(5);
  const slideBy = 1;
  const gap = 20;
  
  const duplicatedCategories = [...categories, ...categories, ...categories];
  const [currentIndex, setCurrentIndex] = useState(categories.length);
  const [isDragging, setIsDragging] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const autoPlayRef = useRef(null);
  const autoPlayDelay = 5000;

  useEffect(() => {
    const updateItemsPerView = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setItemsPerView(2);
      } else if (width < 768) {
        setItemsPerView(3);
      } else if (width < 1024) {
        setItemsPerView(4);
      } else {
        setItemsPerView(5);
      }
    };
    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const slideWidth = 100 / itemsPerView;

  useEffect(() => {
    const handleTransitionEnd = () => {
      if (currentIndex >= categories.length * 2) {
        setTransitionEnabled(false);
        setCurrentIndex(currentIndex - categories.length);
      } else if (currentIndex < categories.length) {
        setTransitionEnabled(false);
        setCurrentIndex(currentIndex + categories.length);
      }
      setTimeout(() => setTransitionEnabled(true), 50);
    };
    const track = trackRef.current;
    if (track) {
      track.addEventListener('transitionend', handleTransitionEnd);
      return () => track.removeEventListener('transitionend', handleTransitionEnd);
    }
  }, [currentIndex, categories.length]);

  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${-currentIndex * slideWidth}%)`;
      trackRef.current.style.transition = transitionEnabled ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
    }
  }, [currentIndex, slideWidth, transitionEnabled]);

  useEffect(() => {
    if (categories.length === 0) return;
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + slideBy);
    }, autoPlayDelay);
    return () => clearInterval(autoPlayRef.current);
  }, [categories.length, slideBy]);

  const handleTouchStart = (e) => {
    clearInterval(autoPlayRef.current);
    const touch = e.touches[0];
    setIsTouching(true);
    setStartX(touch.clientX);
    if (trackRef.current) trackRef.current.style.transition = 'none';
  };

  const handleTouchMove = (e) => {
    if (!isTouching || !containerRef.current) return;
    const touch = e.touches[0];
    const diff = (touch.clientX - startX) / containerRef.current.clientWidth * 100;
    setOffsetX(diff);
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${-(currentIndex * slideWidth) + diff}%)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isTouching) return;
    const threshold = 15;
    if (offsetX < -threshold) setCurrentIndex(prev => prev + slideBy);
    else if (offsetX > threshold) setCurrentIndex(prev => prev - slideBy);
    else {
      if (trackRef.current) {
        trackRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        trackRef.current.style.transform = `translateX(${-currentIndex * slideWidth}%)`;
      }
    }
    setIsTouching(false);
    setOffsetX(0);
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + slideBy);
    }, autoPlayDelay);
  };

  const handleMouseDown = (e) => {
    clearInterval(autoPlayRef.current);
    setIsDragging(true);
    setStartX(e.clientX);
    if (trackRef.current) trackRef.current.style.transition = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    const diff = (e.clientX - startX) / containerRef.current.clientWidth * 100;
    setOffsetX(diff);
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${-(currentIndex * slideWidth) + diff}%)`;
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const threshold = 15;
    if (offsetX < -threshold) setCurrentIndex(prev => prev + slideBy);
    else if (offsetX > threshold) setCurrentIndex(prev => prev - slideBy);
    else {
      if (trackRef.current) {
        trackRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        trackRef.current.style.transform = `translateX(${-currentIndex * slideWidth}%)`;
      }
    }
    setIsDragging(false);
    setOffsetX(0);
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + slideBy);
    }, autoPlayDelay);
  };

  const handleMouseLeave = () => {
    if (isDragging) handleMouseUp();
  };

  if (!categories.length) return null;

  const itemWidth = `calc((100% - ${(itemsPerView - 1) * gap}px) / ${itemsPerView})`;

  return (
    <section className="categories-section w-full">
      <div className="w-full px-2 md:px-4 lg:px-8">
        <div 
          className="product-carousel-container" 
          ref={containerRef}
          style={{ overflow: 'hidden', position: 'relative' }}
        >
          <div
            className="product-carousel-track"
            ref={trackRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              display: 'flex',
              gap: `${gap}px`,
              cursor: isDragging ? 'grabbing' : 'grab',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              touchAction: 'none'
            }}
          >
            {duplicatedCategories.map((cat, idx) => (
              <div 
                key={`${cat.slug}-${idx}`} 
                className="carousel-item-wrapper" 
                style={{ flex: `0 0 ${itemWidth}` }}
              >
                <Link to={`/category/${cat.slug}`} className="category-item" style={{ textDecoration: 'none' }}>
                  <img 
                    src={cat.image} 
                    alt={cat.name} 
                    loading="lazy" 
                    className="category-circle" 
                    style={{ width: '90%', margin: '0 auto' }} 
                  />
                  <p className="category-name">{cat.name}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryCarousel;